/**
 * Same-browser relay for "a ticket was just called/served/no-shown".
 *
 * Why this exists: c10-servicedesk (packages/modules/c10-servicedesk/src/routes/queue.ts)
 * exposes only mutating endpoints for queue tickets — POST /queue-tickets,
 * POST /offices/:code/queue/next, POST /queue-tickets/:id/serve, POST
 * /queue-tickets/:id/no-show. There is NO GET endpoint to list or poll the
 * current queue state for an office. That means a lobby-TV board on a
 * separate device has no API to poll for "who's being served right now".
 *
 * Until c10 grows a real read model for live queue state (e.g. a
 * GET /offices/:code/queue endpoint or a push channel), this module lets the
 * Counter Console broadcast every call/serve/no-show it performs to any
 * Queue Display board open in another tab of the SAME browser (BroadcastChannel,
 * falling back to a localStorage 'storage' event for older environments).
 * This is a genuine, honest same-origin relay — not a simulation of backend
 * data — but it only reaches boards running in this browser profile, not a
 * physically separate lobby TV. See servicedesk-portal's build report for
 * the follow-up this implies for c10.
 */

export interface QueueBusEvent {
  officeCode: string;
  ticketId: string;
  number: string;
  priority: 'NORMAL' | 'ELDERLY' | 'PWD';
  status: 'CALLED' | 'SERVED' | 'NO_SHOW';
  at: string;
}

const CHANNEL_NAME = 'servicedesk.queueBus';
const STORAGE_KEY = 'servicedesk.queueBus.last';

let channel: BroadcastChannel | null = null;
let channelChecked = false;

function getChannel(): BroadcastChannel | null {
  if (channelChecked) return channel;
  channelChecked = true;
  try {
    channel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CHANNEL_NAME);
  } catch {
    channel = null;
  }
  return channel;
}

export function publishQueueEvent(evt: QueueBusEvent): void {
  getChannel()?.postMessage(evt);
  try {
    // Also mirror to localStorage so tabs without BroadcastChannel support
    // (or a channel created after this publish) can still pick it up via the
    // 'storage' event, which only fires in *other* tabs, not this one.
    localStorage.setItem(STORAGE_KEY, JSON.stringify(evt));
  } catch {
    // Private-mode / quota-exceeded — the console still works, the board just misses this beat.
  }
}

export function subscribeQueueEvents(onEvent: (evt: QueueBusEvent) => void): () => void {
  const ch = getChannel();
  const handleMessage = (e: MessageEvent<QueueBusEvent>) => onEvent(e.data);
  ch?.addEventListener('message', handleMessage);

  const handleStorage = (e: StorageEvent) => {
    if (e.key !== STORAGE_KEY || !e.newValue) return;
    try {
      onEvent(JSON.parse(e.newValue) as QueueBusEvent);
    } catch {
      // malformed payload — ignore
    }
  };
  window.addEventListener('storage', handleStorage);

  return () => {
    ch?.removeEventListener('message', handleMessage);
    window.removeEventListener('storage', handleStorage);
  };
}
